import type {
  AuditContextRequest,
  AuditListItem,
  AuditWithEvidence,
  SubmitTextRequest,
  SubmitTextResponse,
  SubmitUrlRequest,
  UploadAndAnalyzeResponse,
  UploadResponse,
} from "@/types";
import i18n from "@/locales";
import {
  AUDIT_UPLOAD_MAX_BODY_BYTES,
  AUDIT_UPLOAD_MAX_FILES,
} from "@vardic/shared/utils/document-upload";
import { ApiError } from "./errors";
import { request, requestBlob, requestVoid, type RequestOptions } from "./request";

const DEFAULT_TEXT_SUBMISSION_TITLE = i18n.t("upload:textInput.defaultFileName");

async function serializeAuditUpload(formData: FormData): Promise<RequestOptions> {
  // Send the same serialization we measure, including its multipart boundary.
  const serialized = new Response(formData);
  const body = await serialized.blob();
  if (body.size > AUDIT_UPLOAD_MAX_BODY_BYTES) {
    throw new ApiError(
      i18n.t("upload:errors.requestTooLarge", {
        maxSize: AUDIT_UPLOAD_MAX_BODY_BYTES / 1024 / 1024,
      }),
      413,
    );
  }
  return { method: "POST", body, headers: serialized.headers };
}

export async function uploadFile(file: File): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append("file", file);
  return request<UploadResponse>(
    "/audits/upload",
    await serializeAuditUpload(formData),
  );
}

/**
 * Fetch an audit by id. The backend `GET /api/audits/:id` route assembles
 * claims, evidence documents/matches/gaps, and assessment-bridge metadata
 * into a single payload (see `AuditReadService.getAudit` server-side), so
 * callers receive an `AuditWithEvidence`-shaped response — there's no
 * separate evidence query to compose.
 */
export async function getAudit(
  auditId: string,
): Promise<AuditWithEvidence> {
  return request<AuditWithEvidence>(`/audits/${auditId}`);
}

export async function listAudits(
  page = 1,
  limit = 20,
): Promise<{ audits: AuditListItem[]; total: number }> {
  return request<{ audits: AuditListItem[]; total: number }>(
    `/audits?page=${page}&limit=${limit}`,
  );
}

export async function uploadAndAnalyze(
  files: File[],
  context?: AuditContextRequest,
  language?: "pl" | "en",
  options?: { groupImages?: boolean },
): Promise<UploadAndAnalyzeResponse> {
  if (files.length > AUDIT_UPLOAD_MAX_FILES) {
    throw new ApiError(
      i18n.t("upload:errors.tooManyFiles", { maxFiles: AUDIT_UPLOAD_MAX_FILES }),
      400,
    );
  }
  const formData = new FormData();
  for (const file of files) formData.append("files", file);
  if (context) formData.append("context", JSON.stringify(context));
  if (language) formData.append("language", language);
  if (options?.groupImages) formData.append("groupImages", "true");
  return request<UploadAndAnalyzeResponse>(
    "/audits/upload-and-analyze",
    await serializeAuditUpload(formData),
  );
}

export async function submitTextForAnalysis(
  request_: SubmitTextRequest,
): Promise<SubmitTextResponse> {
  const { text, title, context, language } = request_;
  return request<SubmitTextResponse>("/audits/submit-text", {
    method: "POST",
    body: { text, title: title ?? DEFAULT_TEXT_SUBMISSION_TITLE, context, language },
    contentType: "json",
  });
}

export async function submitUrlForAnalysis(
  request_: SubmitUrlRequest,
): Promise<SubmitTextResponse> {
  const { url, attestation, context, language } = request_;
  // Uses the generic `request` helper; a fail-loud response carries a
  // `failureCode` which the error normalizer preserves on the thrown
  // ApiError's `response`, so the caller can render the specific state.
  return request<SubmitTextResponse>("/audits/scan-url", {
    method: "POST",
    body: { url, attestation, context, language },
    contentType: "json",
  });
}

export async function downloadAudit(auditId: string): Promise<Blob> {
  return requestBlob(`/audits/${auditId}/download`);
}

export async function downloadParsedText(auditId: string): Promise<Blob> {
  return requestBlob(`/audits/${auditId}/parsed-text`);
}

/**
 * Resolve a short-lived signed URL for an audit's stored screenshot.
 * 404s when the audit has no captured render (mirrors
 * `getMonitoringRunScreenshotUrl` for the monitoring resource).
 */
export async function getAuditScreenshotUrl(
  auditId: string,
): Promise<{ url: string }> {
  return request<{ url: string }>(`/audits/${auditId}/screenshot`);
}

export async function deleteAudit(auditId: string): Promise<void> {
  await requestVoid(`/audits/${auditId}`, { method: "DELETE" });
}

export async function deleteAllAudits(): Promise<{
  deletedCount: number;
  failedFileDeletes?: number;
}> {
  return request<{ deletedCount: number; failedFileDeletes?: number }>(
    "/audits",
    { method: "DELETE" },
  );
}
